/**
 * Opportunity Domain Definition (domain-def@4) — the discovered posting.
 *
 * Opportunities (domain-model.md §3.7) are discovered (driven by the W2
 * SearchConfig), verified, briefed, and graded against the W3 `ProfilerRubric`.
 *
 * Re-derivation — the flagship RAW → PRESENTATION split (llm-workflows §4.3):
 *  - `opportunity.Verified` is a RAW verdict, **typed fields ONLY** — no authored
 *    prose. It carries the derived `register` (du | Sie) read off the posting's
 *    own address form, threaded into the presentation pass.
 *  - `opportunity.Briefed` is NEW — the presented German brief + fit rationale,
 *    carrying `PresentationMeta` + a provenance map.
 *  - `opportunity.Matched` is RAW per-dimension strengths + verbatim quotes; the
 *    score math is domain-owned. `Match.tier` is the shared `MatchTier` primitive.
 *
 * Stream: `Agg::career::{userId}::Opportunity::{opportunityId}`.
 */

import type { DomainDefinitionJSON } from "./schema"

const opportunityDomain = {
  schemaVersion: "domain-def@4",
  name: "Opportunity",
  version: "1.0.0",
  description:
    "Opportunity & Match — discovered/verified/briefed job postings graded against the ProfilerRubric on the authentic axis (fit/social/motivational/goal)",

  types: {
    OpportunityPosting: {
      "+": "reject",
      employer: "NonEmptyString",
      title: "NonEmptyString",
      "location?": "string",
      "url?": "string",
      "snippet?": "string",
      /** The cross-run seen-key (dedupe / recall). */
      "seenKey?": "string",
      /**
       * The job board the posting was found on (`linkedin.com`, `stepstone.de`, …),
       * absent when the posting lives on the company's own site. ADDITIVE (P-B): until
       * this existed, `employer` WAS the URL host, so a board-sourced posting could not
       * tell the platform apart from the hiring company.
       */
      "sourcePlatform?": "string",
      /** The global enrichment-index key of the hiring company (`Enrichment.CompanyProfile.key`). */
      "companyKey?": "string",
    },

    /**
     * VerifyOracle verdict — RAW, typed fields ONLY (llm-workflows §4.3). No
     * authored prose: the German brief moves to `opportunity.Briefed`. The verifier
     * reads the fully SCRAPED page and stamps states / enums / numbers / ISO dates /
     * verbatim quotes, plus the derived address `register` for the presentation pass.
     */
    VerifyVerdict: {
      "+": "reject",
      real: "boolean",
      current: "boolean",
      inScope: "boolean",
      /** Whether the scraped page shows the role still open to apply to. */
      "jobState?": "'open' | 'closed' | 'unknown'",
      /** How real/trustworthy the page looks (a consent-wall/ghost/scam re-post is `suspicious`). */
      "sourceQuality?": "'real' | 'well-described' | 'suspicious'",
      /** Whether the role genuinely matches the candidate's search intent (`mismatch` is a hard non-fit). */
      "fit?": "'match' | 'borderline' | 'mismatch'",
      /** Verbatim disqualifying quote when `fit === 'mismatch'`. */
      "disqualifyingQuote?": "string",
      /** A salary figure / range, verbatim from the page, when stated. */
      "salary?": "string",
      /** ISO date the posting is valid through, when stated. */
      "validThrough?": "Timestamp",
      /** 0..1 — the verifier's calibrated confidence in this extraction + judgement. */
      "confidence?": "UnitInterval",
      /** Address register derived from the posting's own address form — threaded into presentation. */
      register: "AddressRegister",
    },

    /**
     * The presented German brief for the cards/detail (llm-workflows §5.1) — free
     * prose, never fill-in-the-blank, with its citation side-channel + authorship
     * provenance.
     */
    OpportunityBrief: {
      "+": "reject",
      /** 2–4 German sentences: what the role is + the signal behind why it surfaced. */
      brief: "NonEmptyString",
      /** One German sentence grounding the fit verdict, honest about the strongest doubt. */
      "fitNote?": "string",
      provenance: "ProvenanceMap",
      meta: "PresentationMeta",
    },

    Opportunity: {
      "+": "reject",
      id: "Urn",
      owner: "UserId",
      posting: "OpportunityPosting",
      /** Full scraped body — graded in preference to posting.snippet (§6 invariant) */
      "scrapedBody?": "string | null",
      "validThrough?": "Timestamp",
      "verification?": "VerifyVerdict | null",
      /** The presented brief (once authored). */
      "brief?": "OpportunityBrief | null",
      /**
       * The Org this posting belongs to — the normalized employer slug linking the
       * opportunity to its deduplicated Organization stream. Absent when the
       * employer yields no derivable identity.
       */
      "orgRef?": "NonEmptyString",
      /** The derived hiring-identity context, once `opportunity.ContextDerived` ran. */
      "context?": "OpportunityContext | null",
      "bookmarked?": "boolean",
      "dismissed?": "boolean",
      /** Owner hard-removed it — a tombstone (`opportunity.Deleted`); excluded from
       *  recall forever, but the stream is retained for the seen-filter. */
      "deleted?": "boolean",
      createdAt: "Timestamp",
    },

    /**
     * Per-opportunity understanding derived from listing pages (true-employer/role
     * extraction). The domain declared this seam from the start and nothing ever
     * emitted it; WS-E (P-B) fills it with the DETERMINISTIC hiring-identity
     * extraction — no model call, so `prov.activity.associatedWith` is honestly
     * empty and the method rides `activityType`.
     */
    OpportunityContext: {
      "+": "reject",
      id: "Urn",
      owner: "UserId",
      kind: "'opportunity'",
      opportunityRef: "Urn",
      understanding: "string",
      requirements: "string[]",
      /** The hiring company behind the posting, when the page yielded one. */
      "trueEmployer?": "string",
      /** How it was derived: jsonld | ats-url | og-site-name | page-host | body-heading. */
      "employerVia?": "string",
      /** The hiring company's own web domains (never the job board's host). */
      "employerDomains?": "string[]",
      /** The job board the posting was found on, absent for a company's own site. */
      "sourcePlatform?": "string",
      /** The global enrichment-index key for the hiring company. */
      "companyKey?": "string",
      prov: "ProvenanceBundle",
      createdAt: "Timestamp",
    },

    /** Per-dimension match strength with cited claims, verbatim quotes, and named gaps. */
    MatchSignal: {
      "+": "reject",
      dimension: "AlignmentDimension",
      strength: "UnitInterval",
      /** Cited evidence-ledger claims backing this signal */
      claimRefs: "Urn[]",
      /** Verbatim quotes from the posting backing this signal (RAW, no prose). */
      "quotes?": "string[]",
      "gaps?": "string[]",
    },

    Match: {
      "+": "reject",
      id: "Urn",
      owner: "UserId",
      opportunityRef: "Urn",
      "profilerRubricRef?": "Urn | null",
      signals: "MatchSignal[]",
      alignmentDimensions: "AlignmentDimension[]",
      antiValueHits: "string[]",
      score: "UnitInterval",
      /** The shared MatchTier vocabulary (strong | viable | weak | unsuited). */
      tier: "MatchTier",
      /** Derived from CandidateProfile.constraints + SearchContext */
      "locationConstraint?": "string | null",
      prov: "ProvenanceBundle",
      createdAt: "Timestamp",
    },

    // ─── Event payloads ──────────────────────────────────────────

    OpportunityDiscoveredPayload: {
      "+": "reject",
      owner: "UserId",
      opportunity: "Opportunity",
    },

    /** RAW verify verdict attached (typed fields only). */
    OpportunityVerifiedPayload: {
      "+": "reject",
      owner: "UserId",
      opportunityId: "Urn",
      verification: "VerifyVerdict",
      "scrapedBody?": "string | null",
    },

    /** The presented German brief was attached (PRESENTATION pass output). */
    OpportunityBriefedPayload: {
      "+": "reject",
      owner: "UserId",
      opportunityId: "Urn",
      brief: "OpportunityBrief",
    },

    /**
     * The context body rides the payload (`context`), ADDITIVELY. The original shape
     * carried `contextUrn` alone — a reference with nothing on the other end, since no
     * stream ever held an `OpportunityContext`. Readers that only know `contextUrn`
     * keep working; readers that want the derived identity read `context`.
     */
    OpportunityContextDerivedPayload: {
      "+": "reject",
      owner: "UserId",
      opportunityId: "Urn",
      contextUrn: "Urn",
      "context?": "OpportunityContext",
      "prov?": "ProvenanceBundle",
    },

    OpportunityMatchedPayload: {
      "+": "reject",
      owner: "UserId",
      opportunityId: "Urn",
      match: "Match",
    },

    OpportunityBookmarkedPayload: {
      "+": "reject",
      owner: "UserId",
      opportunityId: "Urn",
      bookmarked: "boolean",
    },

    OpportunityDismissedPayload: {
      "+": "reject",
      owner: "UserId",
      opportunityId: "Urn",
      "reason?": "string",
    },

    OpportunityDeletedPayload: {
      "+": "reject",
      owner: "UserId",
      opportunityId: "Urn",
    },
  },

  resources: {
    opportunity: {
      description: "A single discovered opportunity",
      type: "Opportunity",
      urnPattern: "urn:career:opportunity:{opportunityId}",
      fetch: { method: "GET", urlTemplate: "/api/v1/users/{userId}/opportunities/{opportunityId}" },
      authz: { visibility: "user-owned", scopes: ["opportunity:read"] },
    },
    match: {
      description: "The match grading for an opportunity",
      type: "Match",
      urnPattern: "urn:career:match:{opportunityId}",
      fetch: { method: "GET", urlTemplate: "/api/v1/users/{userId}/opportunities/{opportunityId}/match" },
      authz: { visibility: "user-owned", scopes: ["opportunity:read"] },
    },
    opportunityContext: {
      description: "The per-opportunity derived context",
      type: "OpportunityContext",
      urnPattern: "urn:career:context:{opportunityId}:opportunity",
      fetch: { method: "GET", urlTemplate: "/api/v1/users/{userId}/opportunities/{opportunityId}/context" },
      authz: { visibility: "user-owned", scopes: ["opportunity:read"] },
    },
  },

  eventStream: {
    streamPattern: "Agg::career::{userId}::Opportunity::{opportunityId}",
    events: {
      "opportunity.Discovered": {
        description: "A posting was captured via search (source, url, snippet, seen-key)",
        type: "OpportunityDiscoveredPayload",
      },
      "opportunity.Verified": {
        description: "RAW verify verdict — typed fields only, incl. the derived address register; no authored prose",
        type: "OpportunityVerifiedPayload",
      },
      "opportunity.Briefed": {
        description: "The presented German brief + fit rationale was attached (PresentationMeta + provenance map)",
        type: "OpportunityBriefedPayload",
      },
      "opportunity.ContextDerived": {
        description: "A consolidated true-employer/role understanding was derived from listing pages",
        type: "OpportunityContextDerivedPayload",
      },
      "opportunity.Matched": {
        description: "RAW per-dimension strengths + verbatim quotes graded against the ProfilerRubric",
        type: "OpportunityMatchedPayload",
      },
      "opportunity.Bookmarked": {
        description: "The opportunity was bookmarked or un-bookmarked",
        type: "OpportunityBookmarkedPayload",
      },
      "opportunity.Dismissed": {
        description: "The opportunity was dismissed",
        type: "OpportunityDismissedPayload",
      },
      "opportunity.Deleted": {
        description: "The opportunity was hard-removed by the owner (tombstone — excluded from recall forever)",
        type: "OpportunityDeletedPayload",
      },
    },
  },

  authz: {
    scopes: {
      "opportunity:read": { description: "Read the owner's opportunities and matches" },
      "opportunity:write": { description: "Discover, verify, brief, grade, bookmark, or dismiss opportunities" },
    },
    policies: {
      "owner-only": {
        kind: "owner",
        description: "Opportunities and matches are owner-scoped to the calling Subject (law C1)",
      },
    },
    defaults: {
      unauthenticatedAccess: "deny",
      missingRolesBehavior: "deny",
    },
  },
} as const satisfies DomainDefinitionJSON

export default opportunityDomain
